import { useEffect, useState } from "react";
import useTitanApp from "@/hooks/useTitanApp";
import { generatePreConsultAiTemplate } from "@/dispatchers/templateDispatchers";
// Pre-Consult Template Interfaces
export interface PreConsultTemplate {
  id: string;
  name: string;
  description?: string;
  questions: Question[];
  sections: string[];
  active: boolean;
  createdAt?: string;
  responseCount?: number;
  shareLink?: string;
  isIncludeDisclaimer: boolean;
  disclaimerText?: string;
}

export interface Question {
  id: string;
  type: "text" | "single" | "multi" | "image";
  question: string;
  section?: string;
  required: boolean;
  options?: string[];
  multiple?: boolean;
}

// Consultation Template for conversion to pre-consult
interface ConsultationTemplate {
  id: string;
  name: string;
  description: string;
  structuredQuestions: {
    [section: string]: {
      id: number;
      question: string;
      type: "text" | "single" | "multi" | "textarea";
      required: boolean;
      options?: string[];
    }[];
  };
}
type PreConsultResult = {
  questions: Question[];
  name: string;
};

// Mock consultation templates data
export const consultationTemplates: ConsultationTemplate[] = [
  {
    id: "ear-wax",
    name: "Ear Wax Removal",
    description: "Template for ear wax removal procedures",
    structuredQuestions: {
      "Chief Complaint": [
        {
          id: 1,
          question: "What ear-related symptoms are you experiencing?",
          type: "text",
          required: true,
        },
        {
          id: 2,
          question: "How long have you had these symptoms?",
          type: "text",
          required: true,
        },
      ],
      History: [
        {
          id: 3,
          question:
            "Have you used cotton buds or other objects to clean your ears?",
          type: "single",
          required: false,
          options: ["Yes", "No", "Occasionally"],
        },
        {
          id: 4,
          question: "Any pain or discharge from the ear?",
          type: "single",
          required: false,
          options: ["Yes", "No"],
        },
      ],
    },
  },
  {
    id: "travel-health",
    name: "Travel Health Consultation",
    description: "Template for travel health services",
    structuredQuestions: {
      "Travel Details": [
        {
          id: 1,
          question: "Where are you traveling to?",
          type: "text",
          required: true,
        },
        {
          id: 2,
          question: "What is your departure date?",
          type: "text",
          required: true,
        },
        {
          id: 3,
          question: "How long will you be traveling?",
          type: "text",
          required: true,
        },
      ],
      "Health History": [
        {
          id: 4,
          question: "Do you have any current health conditions?",
          type: "text",
          required: false,
        },
        {
          id: 5,
          question: "Are you taking any medications?",
          type: "text",
          required: false,
        },
      ],
    },
  },
  {
    id: "general-consultation",
    name: "General Consultation",
    description: "Template for general medical consultations",
    structuredQuestions: {
      "General Information": [
        {
          id: 1,
          question: "What is the main reason for your visit today?",
          type: "text",
          required: true,
        },
        {
          id: 2,
          question: "When did your symptoms first start?",
          type: "text",
          required: true,
        },
      ],
      "Medical History": [
        {
          id: 3,
          question: "Do you have any chronic medical conditions?",
          type: "multi",
          required: false,
          options: [
            "Diabetes",
            "High Blood Pressure",
            "Heart Disease",
            "Asthma",
            "None of the above",
          ],
        },
        {
          id: 4,
          question: "Are you currently taking any medications?",
          type: "text",
          required: false,
        },
        {
          id: 5,
          question: "Do you have any known allergies?",
          type: "text",
          required: false,
        },
      ],
    },
  },
];

// Default sections for new templates
export const defaultSections = [
  "General Information",
  "Medical History",
  "Current Symptoms",
];

// Mock questions for GPT generation
export const mockGPTQuestions: Question[] = [
  {
    id: "1",
    type: "text",
    question: "Where are you travelling?",
    required: true,
    section: "Symptoms",
  },
  {
    id: "2",
    type: "single",
    question: "Do you have any known allergies?",
    required: true,
    section: "Symptoms",
    options: [
      "Less than 1 week",
      "1-2 weeks",
      "2-4 weeks",
      "More than 1 month",
    ],
  },
  {
    id: "3",
    type: "multi",
    question: "Which of the following apply to you? (Select all that apply)",
    required: false,
    section: "Medical History",
    options: [
      "Diabetes",
      "High Blood Pressure",
      "Heart Disease",
      "Allergies",
      "None of the above",
    ],
  },
  {
    id: "4",
    type: "text",
    question: "List any medications you are currently taking",
    required: false,
    section: "Current Medications",
  },
  {
    id: "5",
    type: "single",
    question:
      "Have you consulted another healthcare provider about this issue?",
    required: true,
    section: "Previous Consultations",
    options: ["Yes, my GP", "Yes, a specialist", "Yes, a pharmacist", "No"],
  },
];

export const usePreConsultData = () => {
  const [consultationTemplates, setTemplates] = useState<
    ConsultationTemplate[]
  >([]);

  const [preConsultTemplatesData, setPreConsultTemplatesData] = useState<
    PreConsultTemplate[]
  >([]);

  const { appState, templateState } = useTitanApp();
  const { darkMode } = appState || {};
  const {
    currentAiTemplates,
    currentPreConsultTemplates,
    isUpdatePreConsultTemplateLoading,
    isSavePreConsultTemplateLoading,
    isPreConsultTemplatesLoading,
    preConsultAiTemplate,
  } = templateState || {};

  // Function to generate AI template for pre-consult
  const generatePreConsultTemplate = async (
    userPrompt: string,
    attachement: string,
  ): Promise<PreConsultResult> => {
    try {
      const payload = {
        userPrompt: userPrompt,
        attachment: attachement,
      };
      const result = await generatePreConsultAiTemplate(payload);
      const questions: Question[] = [];
      let name = "";
      if (
        result &&
        result.templateSections &&
        Array.isArray(result.templateSections)
      ) {
        name = result.templateName;
        let questionId = 1;
        result.templateSections.forEach((section: any) => {
          const sectionName = section?.sectionTitle;

          if (section?.userControls && Array.isArray(section?.userControls)) {
            section?.userControls.forEach((control: any) => {
              const question: Question = {
                id: questionId.toString(),
                type: "text",
                question: control.label || "",
                section: sectionName,
                required: control.required || false,
                options:
                  control.options && control.options.length > 0
                    ? control.options
                    : undefined,
              };

              questions.push(question);
              questionId++;
            });
          }
        });
      }

      return { questions, name };
    } catch (error) {
      console.error("Error generating AI template:", error);
      throw error;
    }
  };

  useEffect(() => {
    if (currentPreConsultTemplates) {
      const mappedPreConsultTemplates: PreConsultTemplate[] = currentPreConsultTemplates.map(
        (t: any) => {
          return {
            id: t._id || "",
            name: t.templateName || "",
            description: t.consultationSummary || "",
            questions: (t.templateSections || [])
              .filter(
                (s: any) =>
                  (s.sectionTitle || "")
                    .toString()
                    .trim()
                    .toLowerCase() !== "disclaimer",
              )
              .flatMap((s: any, sectionIndex: number) =>
                (s.userControls || []).map((uc: any, ucIndex: number) => ({
                  id: `${t._id}-${sectionIndex}-${ucIndex}`,
                  type: uc.type || "text",
                  question: uc.label,
                  section: s.sectionTitle,
                  required: uc.required ?? false,
                  options: uc.options || [],
                  userControlId:
                    uc.userControlId || `${t._id}-${sectionIndex}-${ucIndex}`,
                })),
              ),
            sections: Array.from(
              new Set(
                (t.templateSections || [])
                  .map((s: any) => (s.sectionTitle || "").toString().trim())
                  .filter((st: string) => st.toLowerCase() !== "disclaimer"),
              ),
            ),
            active: t.isActive || false,
            createdAt: t.createdOn || new Date().toISOString(),
            isIncludeDisclaimer: t.isIncludeDisclaimer ?? false,
            disclaimerText: t.disclaimerText || "",
            responseCount: 0,
            shareLink: undefined,
          };
        },
      );
      setPreConsultTemplatesData(mappedPreConsultTemplates);
    }
  }, [currentPreConsultTemplates]);

  useEffect(() => {
    if (currentAiTemplates && currentAiTemplates.length > 0) {
      const descriptionLength = 80;
      const mappedTemplates: ConsultationTemplate[] = currentAiTemplates.map(
        (t: any) => {
          const structuredQuestions: ConsultationTemplate["structuredQuestions"] = {};

          t.templateSections.forEach((s: any) => {
            structuredQuestions[s.sectionTitle] = s.userControls.map(
              (q: any, questionIndex: number) => ({
                id: questionIndex + 1,
                question: q.label,
                type: q.type || "text",
                required: q.required,
                ...(q.options ? { options: q.options } : {}),
              }),
            );
          });

          const fullDescription = t.consultationSummary || "";
          const truncatedDescription =
            fullDescription.length > descriptionLength
              ? fullDescription.substring(0, descriptionLength) + "..."
              : fullDescription;

          return {
            id: t._id,
            name: t.templateName || "",
            description: truncatedDescription,
            structuredQuestions,
          };
        },
      );

      setTemplates(mappedTemplates);
    }
  }, [currentAiTemplates]);

  return {
    consultationTemplates,
    preConsultTemplatesData,
    isPreConsultTemplatesLoading,
    darkMode,
    generatePreConsultTemplate,
    isUpdatePreConsultTemplateLoading,
    isSavePreConsultTemplateLoading,
    preConsultAiTemplate,
  };
};
